import React, { useEffect, useRef, useState, useCallback } from 'react';

const MODEL_URL = 'https://cdn.jsdelivr.net/gh/justadudewhohacks/face-api.js@0.22.2/weights';

// Module-level cache so models are only loaded once per page load
let cachedFaceapi = null;

export default function FacialAccess({ alumnos, onAprobado, onVolver }) {
  const videoRef        = useRef(null);
  const canvasRef       = useRef(null);
  const loopRef         = useRef(null);
  const scanRef         = useRef(null);
  const streamRef       = useRef(null);
  const isProcessingRef = useRef(false);

  const [fase, setFase] = useState('cargando');
  // cargando | camara_error | esperando | detectado | reconociendo | aprobado | negado

  const [progreso, setProgreso]   = useState(0);
  const [alumnoRec, setAlumnoRec] = useState(null);
  const [faceapi, setFaceapi]     = useState(null);

  // ── 1. Cargar modelos ──────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        if (cachedFaceapi) {
          // Models already loaded — skip straight to camera
          if (!cancelled) { setFaceapi(cachedFaceapi); setProgreso(100); iniciarCamara(cachedFaceapi); }
          return;
        }

        // Esperar a que el script CDN termine de cargar
        let intentos = 0;
        while (!window.faceapi && intentos < 20) {
          await new Promise(r => setTimeout(r, 300));
          intentos++;
        }
        if (!window.faceapi) { if (!cancelled) setFase('camara_error'); return; }

        const fa = window.faceapi;
        setProgreso(10);
        await fa.nets.ssdMobilenetv1.loadFromUri(MODEL_URL);
        setProgreso(60);
        await fa.nets.faceLandmark68Net.loadFromUri(MODEL_URL);
        setProgreso(90);
        await fa.nets.faceRecognitionNet.loadFromUri(MODEL_URL);
        setProgreso(100);
        cachedFaceapi = fa;

        if (!cancelled) {
          setFaceapi(fa);
          iniciarCamara(fa);
        }
      } catch (e) {
        console.error('face-api load error:', e);
        if (!cancelled) setFase('camara_error');
      }
    };
    load();
    return () => { cancelled = true; detener(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 2. Cámara ──────────────────────────────────────────────────────
  const iniciarCamara = async (fa) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: 640, height: 480 }
      });
      streamRef.current = stream;
      videoRef.current.srcObject = stream;
      await videoRef.current.play();
      setFase('esperando');
      iniciarLoop(fa);
    } catch {
      setFase('camara_error');
    }
  };

  // ── 3. Loop de detección ───────────────────────────────────────────
  const iniciarLoop = useCallback((fa) => {
    let faceHoldMs = 0;
    const HOLD_NEEDED = 1800; // ms que debe mantenerse la cara para activar escaneo

    const tick = async () => {
      if (isProcessingRef.current) return;
      if (!videoRef.current || videoRef.current.paused || videoRef.current.ended) return;
      const canvas = canvasRef.current;
      if (!canvas) return;

      isProcessingRef.current = true;
      try {
        const opts = new fa.SsdMobilenetv1Options({ minConfidence: 0.55 });
        const detections = await fa.detectAllFaces(videoRef.current, opts).withFaceLandmarks();

        const displaySize = { width: videoRef.current.videoWidth, height: videoRef.current.videoHeight };
        fa.matchDimensions(canvas, displaySize);
        const resized = fa.resizeResults(detections, displaySize);

        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        if (resized.length > 0) {
          // Dibujar recuadro con estilo cyan
          resized.forEach(d => {
            const { x, y, width, height } = d.detection.box;
            ctx.strokeStyle = '#00C8E8';
            ctx.lineWidth = 2.5;
            ctx.shadowBlur = 12;
            ctx.shadowColor = '#00C8E8';
            // Esquinas estilo HUD
            const c = 22;
            ctx.beginPath();
            ctx.moveTo(x, y + c); ctx.lineTo(x, y); ctx.lineTo(x + c, y);
            ctx.moveTo(x + width - c, y); ctx.lineTo(x + width, y); ctx.lineTo(x + width, y + c);
            ctx.moveTo(x + width, y + height - c); ctx.lineTo(x + width, y + height); ctx.lineTo(x + width - c, y + height);
            ctx.moveTo(x + c, y + height); ctx.lineTo(x, y + height); ctx.lineTo(x, y + height - c);
            ctx.stroke();
            ctx.shadowBlur = 0;
          });

          faceHoldMs += 200;
          if (faceHoldMs >= HOLD_NEEDED) {
            faceHoldMs = 0;
            clearInterval(loopRef.current);
            reconocer(fa);
          }
        } else {
          faceHoldMs = 0;
        }
      } catch { /* frame skip */ }
      finally { isProcessingRef.current = false; }
    };

    loopRef.current = setInterval(tick, 200);
  }, []);

  // ── 4. "Reconocimiento" ────────────────────────────────────────────
  const reconocer = (fa) => {
    setFase('reconociendo');

    // Aquí iría la comparación real con descriptores de Supabase.
    // Por ahora simulamos con un delay y elegimos un alumno activo al azar.
    scanRef.current = setTimeout(() => {
      const activos = alumnos.filter(a => a.estado === 'activo' && a.dias_vigentes > 0 && a.revisado !== false);
      if (activos.length > 0) {
        const encontrado = activos[Math.floor(Math.random() * activos.length)];
        setAlumnoRec(encontrado);
        setFase('aprobado');
        onAprobado && onAprobado(encontrado);
      } else {
        setFase('negado');
      }
    }, 2000);
  };

  const detener = () => {
    clearInterval(loopRef.current);
    clearTimeout(scanRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
  };

  const reiniciar = () => {
    detener();
    streamRef.current = null;
    isProcessingRef.current = false;
    setFase('cargando');
    setAlumnoRec(null);
    setProgreso(cachedFaceapi ? 100 : 0);
    const fa = faceapi || cachedFaceapi;
    if (fa) iniciarCamara(fa);
  };

  const handleVolver = () => { detener(); onVolver(); };

  // ── Render ──────────────────────────────────────────────────────────
  return (
    <div className="facial-wrap">
      <button className="back-btn" onClick={handleVolver} style={{ alignSelf: 'flex-start', margin: '0 0 12px 0' }}>
        ← Volver
      </button>

      <div className="facial-title">Acceso <span>Facial</span></div>
      <div className="facial-sub">Colócate frente a la cámara</div>

      {/* Visor de cámara */}
      <div className="facial-cam-box">
        <video
          ref={videoRef}
          className="facial-video"
          muted
          playsInline
          style={{ display: fase === 'camara_error' ? 'none' : 'block' }}
        />
        <canvas ref={canvasRef} className="facial-canvas" />

        {/* Overlay según fase */}
        {fase === 'cargando' && (
          <div className="facial-overlay">
            <div className="facial-spinner" />
            <div className="facial-overlay-text">Cargando modelos de IA…</div>
            <div className="facial-progress-bar">
              <div className="facial-progress-fill" style={{ width: `${progreso}%` }} />
            </div>
          </div>
        )}

        {fase === 'reconociendo' && (
          <div className="facial-overlay scanning">
            <div className="scan-line" />
            <div className="facial-overlay-text cyan">Identificando…</div>
          </div>
        )}

        {fase === 'aprobado' && alumnoRec && (
          <div className="facial-overlay result-ok">
            <div className="facial-result-icon ok">✓</div>
            <div className="facial-result-name">{alumnoRec.nombre} {alumnoRec.apellido}</div>
            <div className="facial-result-plan">{alumnoRec.plan}</div>
            <div className="facial-result-dias">{alumnoRec.dias_vigentes} días vigentes</div>
          </div>
        )}

        {fase === 'negado' && (
          <div className="facial-overlay result-bad">
            <div className="facial-result-icon bad">✗</div>
            <div className="facial-result-name">No reconocido</div>
            <div className="facial-result-plan">Acércate a recepción</div>
          </div>
        )}

        {fase === 'camara_error' && (
          <div className="facial-overlay" style={{ background: 'rgba(10,15,25,.95)' }}>
            <div style={{ fontSize: 40 }}>📷</div>
            <div className="facial-overlay-text" style={{ color: 'var(--red)' }}>Sin acceso a cámara</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', textAlign: 'center', marginTop: 8 }}>
              Verifica los permisos de cámara en el navegador
            </div>
          </div>
        )}
      </div>

      {/* Estado textual */}
      {fase === 'esperando' && (
        <div className="facial-hint">
          <div className="facial-hint-dot" />
          Encuadra tu rostro en la cámara
        </div>
      )}

      {(fase === 'aprobado' || fase === 'negado') && (
        <button className="facial-retry-btn" onClick={reiniciar}>
          🔄 Intentar de nuevo
        </button>
      )}

      {/* Nota demo */}
      <div className="facial-demo-note">
        ⚠ Modo demo — el reconocimiento real requiere registrar el rostro de cada alumno al momento de inscribirse
      </div>
    </div>
  );
}
